import { groupedNumber } from '@/lib/npc-labels';
import { sessionStatusLabel } from '@/lib/session-labels';
import type {
  NpcReplayRow,
  NpcSeriesPlayer,
  NpcSeriesRow,
  NpcSeriesState,
} from '@matter_extractor/shared';
import { NPC_RUN_OUTCOME_LABELS } from '@matter_extractor/shared';

/**
 * What the tables of the NPC section say of replays and series (npc.md §9, §10), PURE.
 *
 * A row lists what the session RECORDED: no linter, no second series beside the first (N10). The
 * expected noyau set against the retained one (#216, npc.md §26) is worded by
 * `npc-expected-synthesis.ts`. So these functions word figures the contract already carries and
 * compute none beyond a sum of the rows on screen.
 */

const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avril',
  'mai',
  'juin',
  'juill.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

/** `1,06 €`, written by hand: `Intl` answers by the locale of the MACHINE. */
export function eurosLabel(value: number): string {
  const [whole = '0', decimals = '00'] = value.toFixed(2).split('.');
  return `${groupedNumber(Number(whole))},${decimals} €`;
}

/** « Claude Sonnet 5.5 » from `claude-sonnet-5-5` or `anthropic/claude-sonnet-5.5`; any other id as is. */
export function modelName(model: string): string {
  const match = /^(?:anthropic\/)?claude-([a-z]+)-(\d+)[-.](\d+)/.exec(model);
  if (match === null) return model;
  const [, family = '', major = '', minor = ''] = match;
  return `Claude ${family.charAt(0).toUpperCase()}${family.slice(1)} ${major}.${minor}`;
}

/** The status cell of a replay: its issue when the series recorded one, else the session's status. */
export function replayStatusLabel(row: Pick<NpcReplayRow, 'outcome' | 'status'>): string {
  return row.outcome === null
    ? sessionStatusLabel(row.status)
    : NPC_RUN_OUTCOME_LABELS[row.outcome];
}

/** « 4 / 7 », or a dash while the session has not got that far. */
export function fractionLabel(done: number | null, total: number | null): string {
  return done === null || total === null ? '-' : `${done} / ${total}`;
}

/** What a replay played with no version of the NPC says of itself (npc.md N17, lot G). */
export const BEFORE_NPC_LABEL = 'joué avant les NPC';

/**
 * The series column of a replay: « Petit banc #204 » is the caller's link; this is the line under it.
 * The label is the link's when the series is there, so the line says it only when there is no
 * series to link to. A replay attached after the fact has the NPC and NO version of it
 * (`npcVersionNumber` null): it says « joué avant les NPC », unless the label of its seeded series
 * already does.
 */
export function replaySeriesLine(
  row: Pick<NpcReplayRow, 'seriesId' | 'seriesLabel' | 'pass' | 'npcVersionNumber'>,
): string {
  const parts: string[] = [];
  if (row.seriesId === null && row.seriesLabel === null) {
    parts.push('hors série');
  } else {
    if (row.pass !== null) parts.push(`passe ${row.pass}`);
    if (row.seriesId === null && row.seriesLabel !== null) parts.push(row.seriesLabel);
  }
  if (row.npcVersionNumber === null && !(row.seriesLabel ?? '').includes('avant les NPC')) {
    parts.push(BEFORE_NPC_LABEL);
  }
  return parts.join(' · ');
}

/**
 * « Dernier rejeu : 28 oct., Le Train v25, en cours, dans une série » - the line under « Regarder le
 * dernier rejeu » on the fiche. The status is the issue when the series recorded one.
 */
export function lastReplayLine(
  row: Pick<
    NpcReplayRow,
    | 'startedAt'
    | 'experienceName'
    | 'experienceVersionNumber'
    | 'status'
    | 'outcome'
    | 'seriesId'
    | 'seriesLabel'
  >,
): string {
  const date = new Date(row.startedAt);
  const day = Number.isNaN(date.getTime())
    ? ''
    : `${date.getDate()} ${MONTHS_SHORT[date.getMonth()] ?? ''}`;
  const experience =
    row.experienceVersionNumber === null
      ? row.experienceName
      : `${row.experienceName} v${row.experienceVersionNumber}`;
  const status =
    row.outcome === null && row.status === 'in-progress' ? 'en cours' : replayStatusLabel(row);
  const series =
    row.seriesLabel !== null
      ? `, dans la série « ${row.seriesLabel} »`
      : row.seriesId !== null
        ? ', dans une série'
        : '';
  return `Dernier rejeu : ${[day, experience, status].filter((part) => part !== '').join(', ')}${series}.`;
}

/** « 12 rejeux · 11,92 € de dispositif · 1,04 € de joueur » - the sum of the rows on screen. */
export function replaysSummary(rows: readonly NpcReplayRow[]): string {
  const device = rows.reduce((total, row) => total + row.costEur, 0);
  const player = rows.reduce((total, row) => total + row.playerCostEur, 0);
  const count = `${rows.length} ${rows.length > 1 ? 'rejeux' : 'rejeu'}`;
  return `${count} · ${eurosLabel(device)} de dispositif · ${eurosLabel(player)} de joueur`;
}

/* ------------------------------------------------------------------ series */

/** The state pill of the Séries tab: « en cours 5/16 », « finie », « arrêtée »... (§9). */
export function seriesStateLabel(
  row: Pick<NpcSeriesRow, 'state' | 'slotCount' | 'finishedCount'>,
): string {
  switch (row.state) {
    case 'running':
      return `en cours ${row.finishedCount}/${row.slotCount}`;
    case 'waiting-claude':
      return 'en attente de Claude';
    case 'finished':
      return 'finie';
    case 'stopped':
      return 'arrêtée';
    case 'interrupted':
      return 'interrompue';
  }
}

/** The tone of that pill, on the register of the interview and session pills. */
export function seriesStateTone(state: NpcSeriesState): 'info' | 'success' | 'neutral' | 'warning' {
  switch (state) {
    case 'running':
      return 'info';
    case 'waiting-claude':
      return 'info';
    case 'finished':
      return 'success';
    case 'stopped':
      return 'neutral';
    case 'interrupted':
      return 'warning';
  }
}

/** « serveur » + « Claude Sonnet 5.5 », or « Claude, préparée » + « par le connecteur du dev ». */
export function seriesPlayerLines(player: NpcSeriesPlayer): { main: string; detail: string } {
  return player.kind === 'server'
    ? { main: 'serveur', detail: modelName(player.model.model) }
    : { main: 'Claude, préparée', detail: 'par le connecteur du dev' };
}

/** What a series was launched on, and the line under it: the bench with its size, or « NPC seul ». */
export function seriesTargetLines(row: Pick<NpcSeriesRow, 'target' | 'passes' | 'slotCount'>): {
  name: string;
  detail: string;
} {
  if (row.target.kind === 'npc') return { name: row.target.npcName, detail: 'NPC seul' };
  const npcs = Math.round(row.slotCount / Math.max(1, row.passes));
  return {
    name: row.target.benchName,
    detail: `${npcs} NPC × ${row.passes} ${row.passes > 1 ? 'passes' : 'passe'}`,
  };
}

/** The line under the experience: « côté développement » or « côté production ». */
export function seriesSideLine(tag: NpcSeriesRow['tag']): string {
  return tag === 'production' ? 'côté production' : 'côté développement';
}

/** Finished replays out of the slots, « 5 » while it runs and « 28 / 30 » once it is over. */
export function seriesFinishedLabel(
  row: Pick<NpcSeriesRow, 'finishedCount' | 'slotCount' | 'state'>,
): string {
  return row.state === 'running' || row.state === 'waiting-claude'
    ? String(row.finishedCount)
    : `${row.finishedCount} / ${row.slotCount}`;
}
